import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { aprApi, cmsApi } from '@commerce-webcomponents/sdk';
import { productApi, ProductData } from '@commerce-product-webcomponents/sdk';
import { Logger } from '@nextgen-web-framework/core';
import { ProductSearch, SearchType, ContentSearch, ContentCategory } from '../models';
import searchApi from '../requests/searchApi';
import {
  SearchState,
  SearchPayloadType,
  BucketField,
  CmsData,
  SearchErrors,
  FetchProductsPayloadType,
  PopularProductsPayloadType,
  ContentSearchPayloadType,
} from './types/searchModelTypes';

const logger = Logger.getInstance({ component: 'Redux slice', product: 'Search' });

export const typeaheadSearchSliceReq = createAsyncThunk<{ results: ProductSearch; error?: boolean }, SearchPayloadType>(
  'commerceSearch/typeaheadSearch',
  async ({ text, warehouseId }: SearchPayloadType) => {
    const payload = {
      warehouseId,
      keywords: text,
      searchType: SearchType.Typeahead,
      offset: '0',
      limit: '3',
    };

    try {
      const results = await searchApi.getSearchResults({ payload });
      return { results };
    } catch (e) {
      logger.log(e);
      return { results: {}, error: true };
    }
  },
);

const fetchContentSearch = async (payload: ContentSearchPayloadType): Promise<ContentSearch> => {
  try {
    return await searchApi.getContentSearch({ payload });
  } catch (e) {
    logger.log(e);
    return {};
  }
};

export const overlayContentSearchSliceReq = createAsyncThunk<ContentSearch, ContentSearchPayloadType>(
  'commerceSearch/overlayContentSearch',
  async ({ category, locale, market, pageSize }: ContentSearchPayloadType) => {
    try {
      const payload = {
        site: market,
        query: '',
        page_size: pageSize,
        next_page_token: '',
        locale,
        category,
      };
      const results = await fetchContentSearch(payload);
      return results;
    } catch (e) {
      logger.log(e);
      return {};
    }
  },
);

export const fulltextSearchSliceReq = createAsyncThunk<{ results: ProductSearch; error?: boolean }, SearchPayloadType>(
  'commerceSearch/fulltextSearch',
  async ({ text, searchMode, warehouseId }, { getState }) => {
    const {
      commerceSearch: { offset, limit, filter, quickFilter },
    } = getState() as { commerceSearch: SearchState };

    const { sort, price, brand, category, solution } = filter;
    const [min, max] = (price || '').split(' - ');
    const priceFilter = min || max ? `${min || ''};${max || ''}` : '';

    const filterParams = [
      ...(priceFilter ? [['filter', `price:${priceFilter}`]] : []),
      ...(brand && brand.length ? [['filter', `brand:${brand.join(';')}`]] : []),
      ...(category ? [['filter', `category:${category}`]] : []),
      ...(solution ? [['filter', `solution:${solution}`]] : []),
      ...(quickFilter ? [['filter', `quickFilters:${quickFilter}`]] : []),
    ];

    const payload: string[][] = Object.entries({
      keywords: text,
      searchType: SearchType.Fulltext,
      offset: `${offset}`,
      limit: `${limit}`,
      ...(warehouseId && { warehouseId }),
      ...(searchMode && { searchMode }),
      ...(sort && { sort }),
    }).concat(filterParams as [string, string][]);

    try {
      const results = await searchApi.getSearchResults({ payload });
      return { results };
    } catch (e) {
      logger.log(e);
      return { results: { offset, total: 0 }, error: true };
    }
  },
);

const fetchProductsByIds = async ({
  idList,
  fulfillmentType,
  warehouseId,
}: FetchProductsPayloadType): Promise<ProductData[]> => {
  try {
    const res = await productApi.getProductByIds({ idList, projections: 'DETAILED', fulfillmentType, warehouseId });
    return res.products ?? [];
  } catch (e) {
    logger.log(e);
    return [];
  }
};

const fetchProductIdsFromAPR = async (fallbackIds?: string[]) => {
  try {
    return await aprApi.getProductIdsByAPR({
      aprContainerKey: 'search-1',
      pageName: 'search',
      channel: 'desktop',
      slots: 3,
    });
  } catch (e) {
    logger.log(e);
    return fallbackIds;
  }
};

export const popularProductsSliceReq = createAsyncThunk<ProductData[], PopularProductsPayloadType>(
  'commerceSearch/popularProducts',
  async ({ fallbackIds, isAprEnabled, ...rest }) => {
    try {
      const productIds = await (isAprEnabled ? fetchProductIdsFromAPR(fallbackIds) : Promise.resolve(fallbackIds));
      if (productIds?.length) {
        return await fetchProductsByIds({ idList: productIds, ...rest });
      }
    } catch (e) {
      logger.log(e);
    }
    return [];
  },
);

export const searchedProductsSliceReq = createAsyncThunk<ProductData[], FetchProductsPayloadType>(
  'commerceSearch/searchedProducts',
  fetchProductsByIds,
);

export const overlayCmsDataSliceReq = createAsyncThunk<CmsData>('commerceSearch/overlayCmsData', async () => {
  try {
    return (await cmsApi.fetchContent({ uId: 'global_search' })) as CmsData;
  } catch (e) {
    logger.log(e);
    return {};
  }
});

export const fulltextContentSearchSliceReq = createAsyncThunk<ContentSearch, ContentSearchPayloadType>(
  'commerceSearch/fulltextContentSearch',
  async ({ query, category, locale, market, pageSize, next_page_token }: ContentSearchPayloadType) => {
    try {
      const payload = {
        site: market,
        page_size: pageSize,
        query,
        next_page_token,
        locale,
        category,
      };
      const results = await fetchContentSearch(payload);
      return results;
    } catch (e) {
      logger.log(e);
      return {};
    }
  },
);

export const filterInitialState = {
  sort: '',
  brand: [],
  price: '',
  category: '',
  solution: '',
};

const initialState: SearchState = {
  offset: 0,
  limit: 8,
  isLoading: false,
  errors: {},
  facets: [],
  filter: filterInitialState,
  availableQuickFilters: [],
  quickFilter: '',
  originallySearchedText: '',
  typeaheadSearchResults: {},
  fulltextSearchResults: {},
  articlesSearchResults: {},
  offersSearchResults: {},
  cmsData: {},
  popularProducts: [],
  searchedProducts: [],
  searchTrackingType: '',
  contentSearchLoaders: {
    isOverlayOffersLoading: false,
    isOverlayArticlesLoading: false,
    isOffersLoading: false,
    isArticlesLoading: false,
  },
  overlayOffersResults: {},
  overlayArticlesResults: {},
};

export const searchSlice = createSlice({
  name: 'commerceSearch',
  initialState,
  reducers: {
    updateFilter: (state, action) => {
      state.filter = action.payload;
    },
    updateQuickFilter: (state, action) => {
      state.quickFilter = action.payload;
    },
    resetSearchResult: (state) => {
      state.facets = [];
      state.filter = filterInitialState;
      state.availableQuickFilters = [];
      state.quickFilter = '';
      state.fulltextSearchResults = {};
      state.offset = 0;
      state.errors.fulltextSearchError = false;
      state.articlesSearchResults = {};
      state.offersSearchResults = {};
    },
    resetFilter: (state) => {
      state.filter = { ...filterInitialState };
    },
    resetSearchError: (state, action: PayloadAction<keyof SearchErrors>) => {
      state.errors[action.payload] = false;
    },
    resetSearchedProducts: (state) => {
      state.searchedProducts = [];
    },
    updateOffset: (state, action) => {
      state.offset = action.payload;
    },
    updateLimit: (state, action) => {
      state.limit = action.payload;
    },
    updateOriginallySearchedText: (state, action) => {
      state.originallySearchedText = action.payload;
    },
    updateSearchTrackingType: (state, action) => {
      state.searchTrackingType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(typeaheadSearchSliceReq.fulfilled, (state, { payload }) => {
      state.typeaheadSearchResults = payload.results;
      state.errors.typeaheadSearchError = payload.error;
    });
    builder.addCase(fulltextSearchSliceReq.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fulltextSearchSliceReq.fulfilled, (state, { payload: { results, error } }) => {
      if (Number(results.offset) === state.offset && (Number(results.offset) === 0 || results.total)) {
        state.fulltextSearchResults = results;
        if (results.total) {
          state.offset += state.limit;
        }
        const quickFilterBucket = results.buckets?.find(({ field }) => field === BucketField.QUICK_FILTERS);
        const availableQuickFilters = quickFilterBucket?.values?.map((ob) => (ob.count && ob.name) || '') || [];
        state.availableQuickFilters = availableQuickFilters;
        const filtersInScope = [BucketField.CATEGORY, BucketField.SOLUTION, BucketField.BRAND, BucketField.PRICE];
        state.facets =
          results.buckets
            ?.filter((obj) => filtersInScope.includes(obj.field as BucketField))
            .sort(
              (f1, f2) =>
                filtersInScope.indexOf(f1.field as BucketField) - filtersInScope.indexOf(f2.field as BucketField),
            ) || [];
      }
      state.isLoading = false;
      state.errors.fulltextSearchError = error;
    });
    builder.addCase(popularProductsSliceReq.fulfilled, (state, action) => {
      state.popularProducts = action.payload;
    });
    builder.addCase(searchedProductsSliceReq.fulfilled, (state, action) => {
      state.searchedProducts = action.payload;
    });
    builder.addCase(overlayCmsDataSliceReq.fulfilled, (state, action) => {
      state.cmsData = action.payload;
    });
    builder.addCase(overlayContentSearchSliceReq.pending, (state, action) => {
      const { arg } = action.meta;
      const { category } = arg;
      if (category === ContentCategory.Article) {
        state.contentSearchLoaders = {
          ...state.contentSearchLoaders,
          isOverlayArticlesLoading: true,
        };
      } else if (category === ContentCategory.Offers) {
        state.contentSearchLoaders = {
          ...state.contentSearchLoaders,
          isOverlayOffersLoading: true,
        };
      }
    });
    builder.addCase(overlayContentSearchSliceReq.fulfilled, (state, action) => {
      const { arg } = action.meta;
      const { category } = arg;
      if (category === ContentCategory.Article) {
        state.contentSearchLoaders = {
          ...state.contentSearchLoaders,
          isOverlayArticlesLoading: false,
        };
        state.overlayArticlesResults = action.payload;
      } else if (category === ContentCategory.Offers) {
        state.contentSearchLoaders = {
          ...state.contentSearchLoaders,
          isOverlayOffersLoading: false,
        };
        state.overlayOffersResults = action.payload;
      }
    });
    builder.addCase(fulltextContentSearchSliceReq.pending, (state, action) => {
      const { arg } = action.meta;
      const { category } = arg;
      if (category === ContentCategory.Article) {
        state.contentSearchLoaders = {
          ...state.contentSearchLoaders,
          isArticlesLoading: true,
        };
      } else if (category === ContentCategory.Offers) {
        state.contentSearchLoaders = {
          ...state.contentSearchLoaders,
          isOffersLoading: true,
        };
      }
    });
    builder.addCase(fulltextContentSearchSliceReq.fulfilled, (state, action) => {
      const { arg } = action.meta;
      const { category } = arg;
      if (category === ContentCategory.Article) {
        state.contentSearchLoaders = {
          ...state.contentSearchLoaders,
          isArticlesLoading: false,
        };
        state.articlesSearchResults = action.payload;
      } else if (category === ContentCategory.Offers) {
        state.contentSearchLoaders = {
          ...state.contentSearchLoaders,
          isOffersLoading: false,
        };
        state.offersSearchResults = action.payload;
      }
    });
  },
});

export const {
  updateFilter,
  updateQuickFilter,
  resetFilter,
  updateOffset,
  updateLimit,
  resetSearchResult,
  resetSearchError,
  resetSearchedProducts,
  updateOriginallySearchedText,
  updateSearchTrackingType,
} = searchSlice.actions;
